import { useCallback } from "react";
import { CanvaButton } from "@design-lab/features/upload-artwork/ui/Canva/CanvaButton";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { isUserLoggedIn } from "@design-lab/shared/lib/auth";
import { Button } from "@design-lab/shared/ui";

type UploadInterfaceProps = {
  shouldOpenCanva: boolean;
  onCanvaAutoOpenComplete: () => void;
  onDeviceClick: () => void;
  getInputProps: () => any;
  onLibraryClick: () => void;
  onCanvaFileReady?: (file: File) => Promise<void>;
};

export function UploadInterface({
  shouldOpenCanva,
  onCanvaAutoOpenComplete,
  onDeviceClick,
  getInputProps,
  onLibraryClick,
  onCanvaFileReady
}: UploadInterfaceProps) {
  const isLoggedIn = isUserLoggedIn();

  const { isDesktop } = useResponsive();

  const handleCanvaFilesReady = useCallback(
    async (files: File[]) => {
      if (files[0] && onCanvaFileReady) {
        await onCanvaFileReady(files[0]);
      }
    },
    [onCanvaFileReady]
  );

  return (
    <>
      <div className="space-y-1 text-center">
        <p className="text-center text-lg font-semibold leading-[28px] text-gray-900 md:text-xl md:leading-[30px]">
          Already have a design?
        </p>
        <p className="md:text-md text-center text-sm font-normal leading-[20px] text-gray-500 md:leading-[24px]">
          Drag & drop or add your image here
          {isLoggedIn && (
            <>
              <br />
              or import from Canva
            </>
          )}
        </p>
      </div>

      <div className="max-w-2xs mt-2 flex w-full flex-col gap-2">
        <Button
          onClick={onDeviceClick}
          className="w-full cursor-pointer rounded-lg bg-gray-900 px-4 text-sm text-white md:px-6 md:text-base"
          data-testid="my-device-button"
          size={isDesktop ? "xl" : "lg"}
        >
          <input {...getInputProps()} />
          My Device
        </Button>

        {isLoggedIn && (
          <Button
            onPress={onLibraryClick}
            variant="outline"
            size={isDesktop ? "xl" : "lg"}
            className="w-full px-2 text-sm shadow-sm shadow-black/[0.1] md:px-4 md:text-base"
            data-testid="my-library-button"
          >
            Library
          </Button>
        )}

        {isLoggedIn && (
          <div className="w-full [&>button]:w-full">
            {onCanvaFileReady && (
              <CanvaButton
                autoOpen={shouldOpenCanva}
                onAutoOpenComplete={onCanvaAutoOpenComplete}
                onFilesReady={handleCanvaFilesReady}
                size={isDesktop ? "xl" : "lg"}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
